import { FieldTypes, ActionConfigs, Sizes, Types, Align, Backdrop, ModalDefaults, TextAreaLabelPlacements } from '../Constants';
import { RadioOptionBuilder } from './RadioOptionBuilder.js';

export class GenericColumnBuilder {
    constructor(width) {
        this.width = width;
        this.fields = [];
        this.labelAlign = null;
        this.rowGap = null;
        this._lastField = null;
    }

    withRowGap(gap) {
        this.rowGap = gap;
        return this;
    }

    withLabelAlign(align) {
        this.labelAlign = align;
        return this;
    }

    addField(field) {
        if (this.labelAlign && !field.labelAlign) field.labelAlign = this.labelAlign;
        this.fields.push(field);
        this._lastField = field;
        return this;
    }

    addHeading(label, options = {}) {
        const config = typeof options === 'string' ? { color: options } : options;
        return this.addField({ type: FieldTypes.HEADING, label, ...config });
    }

    addHiddenField(id, value, options = {}) {
        return this.addField({ id, type: FieldTypes.HIDDEN, value, ...options });
    }

    addHiddenFields(map = {}) {
        Object.entries(map || {}).forEach(([id, value]) => this.addHiddenField(id, value));
        return this;
    }

    addTextField(id, label, options = {}) {
        return this.addField({ id, label, type: FieldTypes.TEXT, ...options });
    }


    addEmailField(id, label, options = {}) {
        return this.addField({ id, label, type: FieldTypes.EMAIL, ...options });
    }

    addNumberField(id, label, options = {}) {
        return this.addField({ id, label, type: FieldTypes.NUMBER, ...options });
    }

    addSelectField(id, label, selectOptions, options = {}) {
        return this.addField({ id, label, type: FieldTypes.SELECT, options: selectOptions, ...options });
    }

    addDatepickerField(id, label, options = {}) {
        return this.addField({ id, label, type: FieldTypes.DATEPICKER, ...options });
    }

    addTextArea(id, label, options = {}) {
        return this.addField({ id, label, type: FieldTypes.TEXTAREA, ...options });
    }

    withLabelPlacement(placement) {
        if (this._lastField && this._lastField.type === FieldTypes.TEXTAREA) {
            const validPlacements = Object.values(TextAreaLabelPlacements);
            if (!placement || validPlacements.includes(placement)) {
                this._lastField.labelPlacement = placement || ModalDefaults.textAreaLabelPlacement;
            }
        }
        return this;
    }

    addCheckbox(id, label, optionsOrFn, options = {}) {
        const field = { id, label, type: "checkbox" };
        if (typeof optionsOrFn === 'function') {
            const builder = {
                withColor: (c) => { field.color = c; return builder; },
                withLabelColor: (c) => { field.labelColor = c; return builder; },
                withValue: (v) => { field.value = v; return builder; },
                checked: (val = true) => { field.checked = val; return builder; }
            };
            optionsOrFn(builder);
            Object.assign(field, options);
        } else {
            Object.assign(field, optionsOrFn, options);
        }
        this.addField(field);
        return this;
    }

    addRadioGroup(groupId, groupLabel, optionsOrFn, optionsOrFn2 = {}) {
        let radioOptions = [];
        if (typeof optionsOrFn === 'function') {
            const builder = new RadioOptionBuilder();
            optionsOrFn(builder);
            radioOptions = builder.build();
        } else {
            radioOptions = optionsOrFn;
        }

        const field = { id: groupId, label: groupLabel, type: "radioGroup", radioOptions };

        if (typeof optionsOrFn2 === 'function') {
            const builder = {
                inline: (val = true) => { field.inline = val; return builder; },
                withColor: (c) => { field.color = c; return builder; },
                withLabelColor: (c) => { field.labelColor = c; return builder; },
                withSelectedValue: (v) => { field.selectedValue = v; return builder; }
            };
            optionsOrFn2(builder);
        } else {
            Object.assign(field, optionsOrFn2);
        }

        return this.addField(field);
    }

    build() {
        return {
            width: this.width,
            fields: this.fields,
            rowGap: this.rowGap
        };
    }
}
